import * as Sentry from '@sentry/react';

/**
 * P5-9 / M7.6 — platform-admin Sentry bootstrap.
 *
 * DSN comes from `VITE_SENTRY_DSN` (resolved at build time by Vite).
 * When unset the SDK does not initialize and Sentry helpers
 * (`Sentry.captureException`) become no-ops.
 *
 * Per D-P5-002:
 *   - Errors only (no tracing or replay).
 *   - 100% sample rate — UI errors are rare in production and the
 *     volume is comfortably within Sentry's free tier.
 */

let initialized = false;

export interface FrontendSentryInitOptions {
    dsn?: string;
    environment?: string;
    release?: string;
}

export function initSentry(options: FrontendSentryInitOptions = {}): boolean {
    if (initialized) return true;
    const dsn = (
        options.dsn ??
        (import.meta.env.VITE_SENTRY_DSN as string | undefined) ??
        ''
    ).trim();
    if (!dsn || !/^https?:\/\//i.test(dsn)) {
        initialized = true;
        return false;
    }
    Sentry.init({
        dsn,
        environment:
            options.environment ??
            (import.meta.env.MODE as string | undefined) ??
            'production',
        release: options.release,
        // Errors only per D-P5-002.
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        beforeSend(event) {
            // Strip request bodies / cookies that may have made it
            // into a breadcrumb.
            if (event.request?.cookies) {
                delete event.request.cookies;
            }
            return event;
        },
    });
    initialized = true;
    return true;
}

export function setSentryUser(input: {
    userId?: string | null;
    email?: string | null;
    role?: string | null;
}): void {
    if (!Sentry.isInitialized()) return;
    if (!input.userId) {
        Sentry.setUser(null);
        return;
    }
    Sentry.setUser({
        id: input.userId,
        ...(input.email ? { email: input.email } : {}),
        ...(input.role ? { username: input.role } : {}),
    });
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
